import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn, UserPlus, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

function AuthLayout({ children, title, subtitle }) {
  return (
    <div style={{
      minHeight: '100vh', paddingTop: 68, display: 'flex', alignItems: 'center',
      background: `radial-gradient(ellipse at 30% 50%, rgba(0,200,255,0.06) 0%, transparent 55%),
                   radial-gradient(ellipse at 70% 30%, rgba(168,85,247,0.06) 0%, transparent 55%),
                   var(--ac-dark)`,
    }}>
      <div className="container" style={{ display: 'flex', justifyContent: 'center', padding: '2rem 1.5rem' }}>
        <div style={{ width: '100%', maxWidth: 460, background: 'var(--ac-card)', border: '1px solid var(--ac-border)', borderRadius: 'var(--radius-xl)', padding: '2.5rem', boxShadow: 'var(--shadow-card)' }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
            <img
              src="/logo.png"
              alt="AlteraCode"
              style={{ height: 72, width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--ac-white)', marginBottom: 6, textAlign: 'center' }}>{title}</h1>
          <p style={{ fontSize: 14, color: 'var(--ac-muted)', textAlign: 'center', marginBottom: '2rem' }}>{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`);
      navigate(from === '/login' || !from ? '/dashboard' : from);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed. Check credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome Back" subtitle="Sign in to access your courses and dashboard">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email Address</label>
          <input className="form-input" type="email" placeholder="you@email.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <div style={{ position: 'relative' }}>
            <input className="form-input" type={showPass ? 'text' : 'password'} placeholder="Your password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required autoComplete="current-password" style={{ paddingRight: 44 }} />
            <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--ac-muted)', cursor: 'pointer' }}>
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div style={{ textAlign: 'right', marginBottom: '1.5rem', marginTop: '-0.5rem' }}>
          <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--ac-cyan)', textDecoration: 'none' }}>Forgot password?</Link>
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', height: 46 }} disabled={loading}>
          {loading ? 'Signing in...' : <><LogIn size={16} /> Sign In</>}
        </button>
      </form>
      <p style={{ fontSize: 14, color: 'var(--ac-muted)', textAlign: 'center', marginTop: '1.5rem' }}>
        Don't have an account? <Link to="/register" style={{ color: 'var(--ac-purple)', textDecoration: 'none', fontWeight: 600 }}>Sign up free</Link>
      </p>
    </AuthLayout>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <AuthLayout title="Check Your Email" subtitle="Almost there!">
      <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
        <CheckCircle size={56} color="var(--ac-green)" style={{ marginBottom: '1rem' }} />
        <p style={{ color: 'var(--ac-text)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
          We've sent a verification link to <strong style={{ color: 'var(--ac-white)' }}>{form.email}</strong>.<br />
          Click it to activate your account.
        </p>
        <Link to="/login" className="btn btn-primary" style={{ justifyContent: 'center' }}>Go to Login</Link>
      </div>
    </AuthLayout>
  );

  return (
    <AuthLayout title="Create an Account" subtitle="Join AlteraCode EduCenter and start your tech journey">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" placeholder="Your full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <input className="form-input" type="email" placeholder="you@email.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input className="form-input" placeholder="+94 71 xxx xxxx" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Password *</label>
          <div style={{ position: 'relative' }}>
            <input className="form-input" type={showPass ? 'text' : 'password'} placeholder="Minimum 8 characters" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required style={{ paddingRight: 44 }} />
            <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--ac-muted)', cursor: 'pointer' }}>
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--ac-muted)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
          By creating an account you agree to our <Link to="/" style={{ color: 'var(--ac-cyan)' }}>Terms of Service</Link> and <Link to="/" style={{ color: 'var(--ac-cyan)' }}>Privacy Policy</Link>.
        </p>
        <button type="submit" className="btn btn-edu" style={{ width: '100%', justifyContent: 'center', height: 46 }} disabled={loading}>
          {loading ? 'Creating Account...' : <><UserPlus size={16} /> Create Account</>}
        </button>
      </form>
      <p style={{ fontSize: 14, color: 'var(--ac-muted)', textAlign: 'center', marginTop: '1.5rem' }}>
        Already have an account? <Link to="/login" style={{ color: 'var(--ac-cyan)', textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
      </p>
    </AuthLayout>
  );
}
